package com.arena_web.ui.sections.header

import androidx.compose.runtime.*
import com.arena_web.data.dto.SportDto
import com.arena_web.core.viewmodel.LoginViewModel
import com.arena_web.di.LocalAppContainer
import com.arena_web.ui.components.dropdown.SportDropdownPanel
import com.arena_web.ui.components.icons.Icon
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.theme.*
import com.arena_web.ui.utils.mBorderRadius
import com.arena_web.ui.utils.mFontSize
import com.arena_web.ui.utils.mGap
import com.arena_web.ui.utils.mHeight
import com.arena_web.ui.utils.mLetterSpacing
import com.arena_web.ui.utils.mLineHeight
import com.arena_web.ui.utils.mPadding
import com.arena_web.ui.utils.mSize
import com.arena_web.ui.utils.mTop
import com.arena_web.ui.utils.mWidth
import com.arena_web.utils.OAuthHelper
import com.arena_web.utils.Storage
import com.arena_web.utils.StorageKeys
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.foundation.layout.*
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.thenIf
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.animation.toAnimation
import com.arena_web.ui.sections.header.components.BrandGlyphDockKeyframes
import com.arena_web.ui.utils.HeroIntroTiming
import kotlinx.browser.window
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img

enum class MobileHeaderAction { Notification, Likes, Activity }

@Composable
fun MobileTopBar(
    onSportChanged: (String) -> Unit = {},
    // When non-null, the center pill is replaced by the page title text
    // (Figma 8285:172272 — Urbanist Medium 24/32 #B4CCF6).
    pageTitle: String? = null,
    // Selects the icon + badge tint for the right-side action button.
    // Notification = bell + primary badge (Figma 8398:85395); Likes = heart
    // + red badge (Figma 8285:375086, used on the People page); Activity =
    // pulse + primary badge (used on the Arena page instead of notifications).
    headerAction: MobileHeaderAction = MobileHeaderAction.Notification
) {
    val ctx = rememberPageContext()
    val container = LocalAppContainer.current

    var isLoggedIn by remember { mutableStateOf(Storage.contains(StorageKeys.ACCESS_TOKEN)) }
    var avatarUrl by remember { mutableStateOf(Storage.get(StorageKeys.AVATAR_URL)) }

    // The profile sheet hosts the avatar upload — refresh the trigger avatar
    // when a new one lands.
    DisposableEffect(Unit) {
        val avatarListener: (org.w3c.dom.events.Event) -> Unit = {
            avatarUrl = Storage.get(StorageKeys.AVATAR_URL)
        }
        window.addEventListener("avatar-updated", avatarListener)
        onDispose { window.removeEventListener("avatar-updated", avatarListener) }
    }

    // Sports — read straight off the container's StateFlow so the cached
    // list (hydrated from localStorage by `ensureSportsLoaded`) is visible
    // as soon as the screen mounts. No need to wait for dropdown open.
    val sportsList by container.sportsList.collectAsState()
    var selectedSportName by remember {
        mutableStateOf(Storage.get(StorageKeys.SELECTED_SPORT_NAME) ?: "All")
    }
    var showSportDropdown by remember { mutableStateOf(false) }

    // Notifications (shown as badge on profile avatar) — driven by the
    // container's StateFlow that AppContainer keeps in sync via the global
    // user WS subscription.
    val notificationCount by container.unreadNotificationCount.collectAsState()
    // Active events/bookings count — badge for the Activity header action.
    val activityCount by container.activityCount.collectAsState()

    // Header bottom sheets — profile menu (avatar tap) + the right-side action.
    var showProfileSheet by remember { mutableStateOf(false) }
    var showNotificationsSheet by remember { mutableStateOf(false) }
    var showActivitySheet by remember { mutableStateOf(false) }

    // Full-screen post-detail sheet opened by tapping the heart action (Likes).
    var showLikesSheet by remember { mutableStateOf(false) }

    // Kick off the SWR fetch once on mount — the cache hydrates the
    // StateFlow synchronously inside `ensureSportsLoaded`, so the dropdown
    // is already populated by the time the user taps the pill.
    LaunchedEffect(Unit) {
        container.ensureSportsLoaded()
    }

    // Notifications count is owned by AppContainer's WS-driven collector;
    // the badge above just reads its StateFlow — no LaunchedEffect needed
    // here. Logout-time reset is also handled in `clearAuthenticatedClients`.

    // Outer Box gives the sport selector an absolute-positioning anchor so it
    // sits at the EXACT viewport horizontal center. Logo + right group flow
    // underneath with SpaceBetween, hugging the edges.
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .mHeight(44)
            .styleModifier {
                property("position", "relative")
                property("flex-shrink", "0")
            }
    ) {
        Row(
            modifier = Modifier.fillMaxWidth().fillMaxHeight(),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically
        ) {
            // Left: Pro Play ring emblem only (no "Arena" wordmark on mobile
            // per Figma 10032:208772). ProPlayLogo SVG ships with
            // preserveAspectRatio="none", so width/height must match its
            // natural 1.47:1 aspect or the ring distorts.
            // On the home page the big hero logo flies INTO this glyph after
            // its assembly intro — start hidden, then dock via the shared
            // BrandGlyphDockKeyframes once the flight lands (HeroIntroTiming
            // .TOTAL_MS). data-brand-glyph marks the FLIP landing target.
            val playIntro = remember {
                window.location.pathname.trimEnd('/').isEmpty()
            }
            Box(
                modifier = Modifier
                    .cursor(Cursor.Pointer)
                    .onClick { ctx.router.navigateTo("/") },
                contentAlignment = Alignment.Center
            ) {
                Img(
                    src = AppIcons.ProPlayLogo,
                    alt = "Pro Play Arena",
                    attrs = Modifier
                        .mWidth(46)
                        .mHeight(31)
                        .attrsModifier { attr("data-brand-glyph", "") }
                        .thenIf(playIntro) {
                            Modifier.opacity(0).animation(
                                BrandGlyphDockKeyframes.toAnimation(
                                    duration = 600.ms,
                                    delay = HeroIntroTiming.TOTAL_MS.ms,
                                    timingFunction = AnimationTimingFunction.EaseOut,
                                    fillMode = AnimationFillMode.Both,
                                )
                            )
                        }
                        .toAttrs()
                )
            }

            // Right: Notification + Profile (logged in) or Auth buttons.
            if (isLoggedIn) {
                // Long sport names (Basketball / Volleyball) push the centred
                // Sport pill close to the right group — collapse to the tight
                // 4 px gap so the pill has room. Other sports get a slightly
                // wider gap so Notification floats a bit left of Profile.
                val isLongSport = selectedSportName.equals("Basketball", ignoreCase = true) ||
                    selectedSportName.equals("Volleyball", ignoreCase = true)
                val rightGroupGap = if (isLongSport) 4 else 12
                Row(
                    modifier = Modifier.mGap(rightGroupGap),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    MobileNotificationButton(
                        badgeCount = when (headerAction) {
                            MobileHeaderAction.Activity -> activityCount
                            else -> notificationCount
                        },
                        action = headerAction,
                        onClick = {
                            showSportDropdown = false
                            when (headerAction) {
                                // The heart (Likes) opens the full-screen
                                // post-detail sheet (Figma 8282:171340).
                                MobileHeaderAction.Likes -> showLikesSheet = true
                                MobileHeaderAction.Notification -> showNotificationsSheet = true
                                MobileHeaderAction.Activity -> showActivitySheet = true
                            }
                        }
                    )
                    MobileProfileButton(
                        avatarUrl = avatarUrl,
                        onClick = {
                            showSportDropdown = false
                            showProfileSheet = true
                        }
                    )
                }
            } else {
                // Full reload after login so all pages re-read auth state
                // from storage (the `remember { Storage.contains(...) }`
                // initialisers in Arena/Feed/People only run once per mount).
                val onLoggedIn: () -> Unit = {
                    kotlinx.browser.window.location.reload()
                }
                Row(
                    modifier = Modifier.mGap(6),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    MobileGoogleButton(onLoggedIn = onLoggedIn)
                    MobileAppleButton(onLoggedIn = onLoggedIn)
                }
            }
        }

        // Centre: either the page title text (Figma 8285:172272) or the
        // sport selector pill, pinned to the EXACT viewport horizontal centre
        // via left:50% + translateX(-50%). z-index lifts the wrapper above
        // sibling rows so the open sport dropdown isn't trapped by the
        // transform-induced stacking context.
        Box(
            modifier = Modifier.styleModifier {
                property("position", "absolute")
                property("left", "50%")
                property("top", "50%")
                property("transform", "translate(-50%, -50%)")
                property("z-index", "1100")
            }
        ) {
            if (pageTitle != null) {
                SpanText(
                    pageTitle,
                    modifier = Modifier
                        .fontSize(24.px)
                        .fontWeight(500)
                        .lineHeight(32.px)
                        .letterSpacing(0.24.px)
                        .color(AppColors.Text.Primary)
                        .styleModifier {
                            property("font-family", "'Urbanist', sans-serif")
                            property("white-space", "nowrap")
                        }
                )
            } else {
                MobileSportSelector(
                    selectedSportName = selectedSportName,
                    sportsList = sportsList,
                    showDropdown = showSportDropdown,
                    onToggle = { showSportDropdown = !showSportDropdown },
                    onSelect = { sport ->
                        Storage.save(StorageKeys.SELECTED_SPORT_ID, sport.id)
                        Storage.save(StorageKeys.SELECTED_SPORT_NAME, sport.name)
                        selectedSportName = sport.name
                        onSportChanged(sport.name)
                        showSportDropdown = false
                    }
                )
            }
        }
    }

    // Full-screen "Interactions" sheet (Figma 8289:3249) — overlays the page
    // when the heart action is tapped. position:fixed inside BottomSheet, so
    // mounting it here is enough to cover the whole viewport.
    if (showLikesSheet) {
        com.arena_web.ui.components.overlay.MobileInteractionsSheet(
            sportName = selectedSportName,
            onDismiss = { showLikesSheet = false }
        )
    }

    // Phone counterparts of the desktop header dropdowns — see MobileHeaderSheets.
    if (showNotificationsSheet) {
        MobileNotificationsSheet(onDismiss = { showNotificationsSheet = false })
    }
    if (showActivitySheet) {
        MobileActivitySheet(onDismiss = { showActivitySheet = false })
    }
    if (showProfileSheet) {
        MobileProfileSheet(onDismiss = { showProfileSheet = false })
    }
}

// =============================================================================
// Sport Selector (matches Figma design)
// =============================================================================

@Composable
private fun MobileSportSelector(
    selectedSportName: String,
    sportsList: List<SportDto>,
    showDropdown: Boolean,
    onToggle: () -> Unit,
    onSelect: (SportDto) -> Unit
) {
    val t = LocalAppStrings.current
    val sportColors = SportColors.forSport(selectedSportName.lowercase())

    // Stable DOM id so document-level mousedown can detect clicks outside
    // both the trigger pill and the menu. Same trick the Arena-mobile
    // location dropdown uses (ArenaContentMobile).
    val rootId = remember { "mobile-sport-${kotlin.js.Date.now().toLong()}-${kotlin.random.Random.nextInt(0, 1_000_000)}" }
    DisposableEffect(showDropdown) {
        if (!showDropdown) return@DisposableEffect onDispose {}
        val handler: (org.w3c.dom.events.Event) -> Unit = { ev ->
            val target = ev.target as? org.w3c.dom.Node
            val root = kotlinx.browser.document.getElementById(rootId)
            if (target != null && root != null && !root.contains(target)) {
                onToggle()
            }
        }
        kotlinx.browser.document.addEventListener("mousedown", handler)
        onDispose { kotlinx.browser.document.removeEventListener("mousedown", handler) }
    }

    Box(modifier = Modifier.id(rootId).position(Position.Relative)) {
        Row(
            modifier = Modifier
                .mHeight(44)
                .mBorderRadius(26)
                .mPadding(8, 18)
                .mGap(12)
                .cursor(Cursor.Pointer)
                .styleModifier {
                    // Figma 10032:208784 — Interactive/Secondary fill +
                    // Button/border/gradient/primary stroke. No backdrop-filter
                    // on this pill, so the border-box trick is fine (mirrors
                    // the desktop SportSelectorPill).
                    property("border", "0.3px solid transparent")
                    property(
                        "background",
                        "${AppGradients.InteractiveSecondary} padding-box, " +
                            "linear-gradient(0deg, #02040B, #02040B) padding-box, " +
                            "${AppGradients.ButtonBorderGradientPrimary} border-box"
                    )
                }
                .onClick { onToggle() },
            verticalAlignment = Alignment.CenterVertically
        ) {
            SpanText(
                t.headerSportLabel,
                modifier = Modifier
                    .mFontSize(16)
                    .fontWeight(500)
                    .mLineHeight(18)
                    .mLetterSpacing(0.2)
                    .color(AppColors.Text.Secondary)
            )
            Row(
                modifier = Modifier.mGap(8),
                verticalAlignment = Alignment.CenterVertically
            ) {
                SpanText(
                    selectedSportName.replaceFirstChar { it.uppercase() },
                    modifier = Modifier
                        .mFontSize(16)
                        .fontWeight(500)
                        .mLineHeight(18)
                        .mLetterSpacing(0.2)
                        .color(sportColors.metricsPrimary)
                )
                // Figma renders chevron-down inside a 16px slot at its natural
                // 15.75 × 9 aspect. Raw px overrides Icon's default sWidth/sHeight,
                // which would be ≈ 6×3.6 px on mobile (--scale ≈ 0.4 below 768).
                Icon(
                    src = AppIcons.ChevronDown,
                    modifier = Modifier.mWidth(12).mHeight(7)
                )
            }
        }

        if (showDropdown && sportsList.isNotEmpty()) {
            // Shared with desktop AppHeader's SportSelectorPill — same panel,
            // same icon set, same colour rules. See `SportDropdownPanel`.
            SportDropdownPanel(
                selectedSportName = selectedSportName,
                sportsList = sportsList,
                onSelect = onSelect,
                modifier = Modifier
                    .position(Position.Absolute)
                    .mTop(50)
                    .zIndex(1100)
                    .styleModifier {
                        property("left", "50%")
                        property("transform", "translateX(-50%)")
                    }
            )
        }
    }
}

// =============================================================================
// Notification button with unread badge — Figma 8398:85395.
// =============================================================================

@Composable
private fun MobileNotificationButton(
    badgeCount: Int,
    action: MobileHeaderAction = MobileHeaderAction.Notification,
    onClick: () -> Unit = {}
) {
    val sportColors = LocalSportColors.current
    val iconSrc = when (action) {
        MobileHeaderAction.Notification -> AppIcons.Notifications
        MobileHeaderAction.Likes -> AppIcons.Heart
        MobileHeaderAction.Activity -> AppIcons.Pulse
    }
    // Likes uses the loss/red accent (Figma 8285:375086); notifications and
    // activity use the active sport's primary colour.
    val badgeBg = when (action) {
        MobileHeaderAction.Likes -> "#FF4242"
        else -> sportColors.primary.toString()
    }

    Box(modifier = Modifier.position(Position.Relative)) {
        Box(
            modifier = Modifier
                .mSize(36)
                .mBorderRadius(100)
                .cursor(Cursor.Pointer)
                .onClick { onClick() }
                .styleModifier {
                    property("border", "0.3px solid transparent")
                    property(
                        "background",
                        "${AppGradients.InteractiveSecondary} padding-box, " +
                            "linear-gradient(0deg, #02040B, #02040B) padding-box, " +
                            "${AppGradients.ButtonBorderGradientPrimary} border-box"
                    )
                },
            contentAlignment = Alignment.Center
        ) {
            Icon(src = iconSrc, modifier = Modifier.mSize(16))
        }

        if (badgeCount > 0) {
            Box(
                modifier = Modifier
                    .mSize(12)
                    .mBorderRadius(100)
                    .styleModifier { property("background-color", badgeBg) }
                    .position(Position.Absolute)
                    .top(0.px)
                    .right(0.px)
                    .overflow(Overflow.Hidden),
                contentAlignment = Alignment.Center
            ) {
                SpanText(
                    badgeCount.toString(),
                    modifier = Modifier
                        .mFontSize(8)
                        .color(AppColors.PrimaryWhite)
                        .styleModifier {
                            property("font-family", "'Sora', sans-serif")
                            property("line-height", "1")
                            property("transform", "translateY(0.5px)")
                        }
                )
            }
        }
    }
}

// =============================================================================
// Profile Button — avatar that opens the profile bottom sheet (the phone
// counterpart of the desktop ProfilePill dropdown, see MobileHeaderSheets).
// =============================================================================

@Composable
private fun MobileProfileButton(
    avatarUrl: String?,
    onClick: () -> Unit
) {
    Box(
        modifier = Modifier
            .mSize(36)
            .mBorderRadius(100)
            .overflow(Overflow.Hidden)
            .backgroundColor(AppColors.Text.Primary)
            .cursor(Cursor.Pointer)
            .onClick { onClick() },
        contentAlignment = Alignment.Center
    ) {
        if (avatarUrl != null) {
            Div(attrs = {
                ref { el ->
                    el.innerHTML = "<img src=\"$avatarUrl\" referrerpolicy=\"no-referrer\" " +
                        "onerror=\"this.style.display='none'\" " +
                        "style=\"width:100%;height:100%;object-fit:cover;\" />"
                    onDispose {}
                }
                style { property("width", "100%"); property("height", "100%") }
            })
        } else {
            Icon(src = AppIcons.Profile, modifier = Modifier.mSize(18))
        }
    }
}

// =============================================================================
// Mobile Auth Buttons (compact)
// =============================================================================

@Composable
private fun MobileGoogleButton(onLoggedIn: () -> Unit) {
    val container = LocalAppContainer.current
    val loginViewModel = remember {
        LoginViewModel(
            oauthLoginUseCase = container.getOAuthLoginUseCase(),
            container = container
        )
    }
    var isLoading by remember { mutableStateOf(false) }

    Box(
        modifier = Modifier
            .mSize(36)
            .mBorderRadius(18)
            .cursor(if (isLoading) Cursor.NotAllowed else Cursor.Pointer)
            .styleModifier {
                property("border", "0.3px solid transparent")
                property(
                    "background",
                    "${AppGradients.InteractiveSecondary} padding-box, " +
                        "linear-gradient(0deg, #02040B, #02040B) padding-box, " +
                        "${AppGradients.ButtonBorderGradientPrimary} border-box"
                )
            }
            .onClick {
                if (!isLoading) {
                    isLoading = true
                    OAuthHelper.triggerGoogleSignIn(
                        onSuccess = { googleUser ->
                            val lang = OAuthHelper.localeToLanguageName(googleUser.locale)
                            loginViewModel.loginWithOAuth(
                                provider = "google",
                                providerId = googleUser.sub,
                                email = googleUser.email,
                                fullName = googleUser.name,
                                avatarUrl = googleUser.picture,
                                languages = lang?.let { listOf(it) },
                                onSuccess = { onLoggedIn() }
                            )
                        },
                        onError = {
                            console.error("Google sign-in error: $it")
                            isLoading = false
                        }
                    )
                }
            },
        contentAlignment = Alignment.Center
    ) {
        Icon(src = AppIcons.Google, modifier = Modifier.mSize(16))
    }
}

@Composable
private fun MobileAppleButton(onLoggedIn: () -> Unit) {
    val container = LocalAppContainer.current
    val loginViewModel = remember {
        LoginViewModel(
            oauthLoginUseCase = container.getOAuthLoginUseCase(),
            container = container
        )
    }
    var isLoading by remember { mutableStateOf(false) }

    Box(
        modifier = Modifier
            .mSize(36)
            .mBorderRadius(18)
            .cursor(if (isLoading) Cursor.NotAllowed else Cursor.Pointer)
            .styleModifier {
                property("border", "0.3px solid transparent")
                property(
                    "background",
                    "${AppGradients.InteractiveSecondary} padding-box, " +
                        "linear-gradient(0deg, #02040B, #02040B) padding-box, " +
                        "${AppGradients.ButtonBorderGradientPrimary} border-box"
                )
            }
            .onClick {
                if (!isLoading) {
                    isLoading = true
                    OAuthHelper.signInWithApple(
                        onSuccess = { email, name, providerId ->
                            loginViewModel.loginWithOAuth(
                                provider = "apple",
                                providerId = providerId,
                                email = email,
                                fullName = name,
                                onSuccess = { onLoggedIn() }
                            )
                        },
                        onError = {
                            console.error("Apple sign-in error: $it")
                            isLoading = false
                        }
                    )
                }
            },
        contentAlignment = Alignment.Center
    ) {
        Icon(src = AppIcons.Apple, modifier = Modifier.mSize(14))
    }
}

